import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/learner/ui/Card'
import { Button3D } from '@/components/learner/ui/Button3D'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { GameCard } from '@/components/learner/playground'
import { getGameEntry } from '@quebon/games'
import { useLearnerGames } from '@/hooks/learner/useLearnerGames'
import { getLearnerErrorMessage } from '@/utils/errorMessage'
import type { GameWithStatus } from '@/api/learnerGames'

export default function Playground() {
  const { t, i18n } = useTranslation(['learner', 'common'])
  const navigate = useNavigate()

  const { data, isLoading, error, refetch } = useLearnerGames()

  // 백엔드는 title/imageUrl을 비워 보내므로 @quebon/games 레지스트리로 보강한다.
  // (MSW 개발 모드는 이미 값을 채워 보내므로 `||`로 보존)
  const items = useMemo<GameWithStatus[]>(
    () =>
      (data ?? [])
        .filter((g) => g.isUnlocked)
        .map((g) => {
          const entry = getGameEntry(g.key)
          const registryTitle =
            i18n.language === 'en' ? entry?.en?.name : entry?.ko?.name
          const registryDesc =
            i18n.language === 'en' ? entry?.en?.desc : entry?.ko?.desc
          return {
            ...g,
            title: g.title || registryTitle || g.key,
            description: g.description || registryDesc,
            imageUrl: g.imageUrl || entry?.image || '',
            type: g.type ?? entry?.type,
          }
        }),
    [data, i18n.language],
  )

  function handleCardClick(game: GameWithStatus) {
    navigate(`/playground/${game.key}`)
  }

  if (isLoading) {
    return <LearnerLoading />
  }

  if (error) {
    return (
      <Frame>
        <Card
          size="md"
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center gap-4 py-8 text-center"
        >
          <p className="text-lc-body-2 text-lc-gray-700">
            {t('playground.error')} — {getLearnerErrorMessage(error, t)}
          </p>
          <Button3D variant="lightblue" size="M" onClick={() => refetch()}>
            {t('playground.errors.retry')}
          </Button3D>
        </Card>
      </Frame>
    )
  }

  return (
    <Frame labelledBy="playground-heading">
      <div className="flex flex-col items-start gap-2">
        <h1
          id="playground-heading"
          className="text-lc-v3-h3 font-bold text-lc-v3-gray-950"
        >
          {t('playground.title')}
        </h1>
        <p className="text-lc-v3-body-1 font-medium text-lc-v3-gray-700">
          {t('playground.subtitle')}
        </p>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title={t('playground.empty.title')}
          desc={t('playground.empty.desc')}
        />
      ) : (
        <div className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {items.map((game) => (
            <GameCard key={game.key} game={game} onClick={handleCardClick} />
          ))}
        </div>
      )}
    </Frame>
  )
}

function Frame({
  children,
  labelledBy,
}: {
  children: React.ReactNode
  labelledBy?: string
}) {
  return (
    <section
      aria-labelledby={labelledBy}
      className="mx-auto flex w-full max-w-[1040px] flex-col gap-10 px-4 pb-10 pt-6 animate-lc-rise md:px-10 md:pt-10"
    >
      {children}
    </section>
  )
}

function EmptyState({
  title,
  desc,
}: {
  title: string
  desc: string
}) {
  return (
    <div className="flex min-h-60 h-80 flex-col items-center justify-center rounded-[20px] bg-lc-bg-tertiary px-4 py-10 text-center text-lc-v3-gray-500">
      <h2 className="text-lc-v3-h4 font-bold">{title}</h2>
      <p className="mt-2 text-lc-v3-body-1 font-medium">{desc}</p>
    </div>
  )
}
